import { FIELD_TYPE } from "@saleor/components/FormField/constants";
import { getSalesPartnerMapping } from "@saleor/createOrChangeNotRefContract/components/HeaderTab/Tab/NotRefSalesPartner/utils";
import useThailandAddress from "@saleor/createOrChangeNotRefContract/hooks/useThailandAddress";
import {
  useGetCountryMasterDataLazyQuery,
  useSearchSuggestionSoldTosCipLazyQuery,
  useSoldToHeaderInfoCipLazyQuery
} from "@saleor/graphql";
import { mapEdgesToItems } from "@saleor/utils/maps";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import useStyles from "./styles";
const useOneTimeCustomer = (formikOtcSoldTo, formik, visible) => {
  useEffect(() => {
    formikOtcSoldTo.setValues(prev => ({
      ...prev,
      soldToCode:
        formik?.values?.defaultOtcSoldToCode || formik?.values?.soldToCode,
      soldToDisplayText: formik?.values?.defaultOtcSoldToText
    }));
  }, [formik?.values]);
  const [loadPaymentData, setLoadPaymentData] = useState<boolean>(false);
  function customOnHandleChange(e) {
    return formikOtcSoldTo?.setValues(prev => ({
      ...prev,
      soldToCode: e.value,
      soldToDisplayText: e.title
    }));
  }

  const [headerData] = useSoldToHeaderInfoCipLazyQuery({
    fetchPolicy: "no-cache",

    onCompleted: data => {
      const headerData = data?.soldToHeaderInfoCip;
      if (!formik?.values?.isOneTimeCustomer) {
        formik.setValues(prev => ({
          ...prev,
          paymentTermList: headerData?.paymentTerm ?? [],
          salesGroupList: headerData?.saleGroup ?? [],
          orderType: headerData?.orderType ?? "",
          salesGroupCode: headerData?.defaultSaleGroup?.code ?? "",
          headerNote1: headerData?.headerNote1Th?.textLine ?? "",
          internalCommentsToWarehouse:
            headerData?.commentsToWarehouseTh?.textLine ?? ""
        }));
        formikOtcSoldTo.setValues(prev => ({
          ...prev,
          taxClass: headerData?.taxClassification
        }));
      }
    }
  });
  const [
    getDataSuggestions,
    { data: fetchDataSuggestions, loading: loadingDataSuggestion }
  ] = useSearchSuggestionSoldTosCipLazyQuery({
    fetchPolicy: "no-cache",
    variables: {
      first: 100,
      saleOrg: formik?.values?.salesOrganizationCode,
      distributionChannel: formik?.values?.distributionChannelCode,
      division: formik?.values?.divisionCode,
      oneTimeCustomer: true
    }
  });
  const dataOptions = (
    mapEdgesToItems(fetchDataSuggestions?.searchSuggestionSoldTosCip) || []
  ).map(data => ({
    title: data.displayText,
    value: data.code
  }));
  useEffect(() => {
    if (formikOtcSoldTo?.values?.soldToCode?.length >= 7 && visible) {
      getDataSuggestions();
      setLoadPaymentData(true);
    }
  }, [formikOtcSoldTo?.values?.soldToCode, visible]);
  useEffect(() => {
    if (loadPaymentData) {
      headerData({
        variables: {
          soldTo:
            formikOtcSoldTo?.values?.soldToCode ||
            formik?.values?.defaultOtcSoldToCode,
          saleOrg: formik?.values?.salesOrganizationCode,
          distributionChannel: formik?.values?.distributionChannelCode,
          division: formik?.values?.divisionCode
        }
      });
    }
  }, [loadPaymentData]);
  const intl = useIntl();
  const classes = useStyles();
  const paymentTermList = getSalesPartnerMapping(
    formik?.values?.paymentTermList
  );
  const isOtherCountry = formikOtcSoldTo?.values?.countryCode !== "TH";
  const {
    subDistrict,
    district,
    city,
    zipCode,
    selectedZipCode
  } = useThailandAddress(formikOtcSoldTo);
  const isDisableFiled = fieldvalue => {
    if (fieldvalue?.length === 0) {
      return true;
    } else {
      return false;
    }
  };
  const [
    getCountryList,
    { data: countryCodeNameList }
  ] = useGetCountryMasterDataLazyQuery({ fetchPolicy: "no-cache" });
  useEffect(() => {
    if (visible) {
      getCountryList();
    }
  }, [visible]);

  useEffect(() => {
    const selectedCountry = countryCodeNameList?.getCountryMasterData?.find(
      item => item?.countryCode === formikOtcSoldTo?.values?.countryCode
    );

    if (selectedCountry) {
      formikOtcSoldTo.setFieldValue(
        "countryName",
        selectedCountry?.countryName?.toUpperCase()
      );
    }
  }, [formikOtcSoldTo?.values?.countryCode, countryCodeNameList]);
  const taxClassificationList: Array<{ value: string; label: string }> = [
    { value: "0", label: "0 : Liable for taxes 0%" },
    { value: "1", label: "1 : Liable for Tax" }
  ];
  const languageList = isOtherCountry
    ? [{ value: "en", label: "EN" }]
    : [
        { value: "en", label: "EN" },
        { value: "th", label: "TH" }
      ];
  const countryList =
    countryCodeNameList?.getCountryMasterData?.map(item => ({
      label: `${item?.countryCode} - ${item?.countryName?.toUpperCase()}`,
      value: item?.countryCode
    })) || [];
  useEffect(() => {
    if (formikOtcSoldTo?.values?.countryCode !== "TH") {
      formikOtcSoldTo.setFieldValue("language", "en");
    }
  }, [formikOtcSoldTo?.values?.countryCode]);
  const formOneTimeData = useMemo(
    () => [
      {
        title: formik.values.isOneTimeCustomer
          ? intl.formatMessage({ defaultMessage: "Cust. Code" })
          : intl.formatMessage({ defaultMessage: "One-time Code" }),
        name: "soldToCode",
        required: true,
        placeholder: intl.formatMessage({
          defaultMessage: "ค้นหา One-time Code"
        }),
        mappingDataSuggestions: {
          loadingDataSuggestion,
          getDataSuggestions,
          fetchDataSuggestions,
          searchField: "search",
          dataOptions
        },
        type: formik.values.isOneTimeCustomer
          ? FIELD_TYPE.SINGLE_INPUT
          : FIELD_TYPE.SUGGESTION_INPUT,
        md: 12,
        isSearchIcon: true,
        displayText: formikOtcSoldTo?.values?.soldToDisplayText,
        isDisabled: formik.values.isOneTimeCustomer,
        customOnChange: e => customOnHandleChange(e)
      },
      {
        title: intl.formatMessage({ defaultMessage: "Payment Term" }),
        name: "paymentTerm",
        required: true,
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ Payment Term"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: paymentTermList,
        md: 6,
        hideField: formik.values.isOneTimeCustomer
      },
      {
        title: intl.formatMessage({ defaultMessage: "Tax Classification" }),
        name: "taxClass",
        required: true,
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ Tax Classification"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: taxClassificationList,
        md: 6,
        hideField: formik.values.isOneTimeCustomer
      },
      {
        title: intl.formatMessage({ defaultMessage: "Cust. Name" }),
        name: "name1",
        required: true,
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุชื่อลูกค้า หรือ ชื่อบริษัท"
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        md: 12,
        hint:
          formikOtcSoldTo?.values?.name1?.length > 0 ? (
            <div
              style={{
                textAlign: "end",
                color: "#696969",
                marginRight: "-14px",
                marginBottom: "-22px"
              }}
            >{`${formikOtcSoldTo?.values?.name1?.length || 0}/70`}</div>
          ) : null,
        maxLength: 70
      },
      { type: "Headers", title: "Address" },
      {
        type: "Button",
        title: intl.formatMessage({
          defaultMessage: "ค้นหาข้อมูล ผู้จดทะเบียนภาษีมูลค่าเพิ่ม"
        })
      },
      {
        title: intl.formatMessage({ defaultMessage: "Country" }),
        name: "countryCode",
        required: true,
        type: FIELD_TYPE.SINGLE_SELECT,
        options: countryList,
        md: 2,
        labelParser: value => {
          if (typeof value === "string") {
            return value.split("-")[0].trim();
          } else {
            return value;
          }
        }
      },
      {
        name: "countryName",
        type: FIELD_TYPE.SINGLE_INPUT,
        isDisabled: true,
        md: formik.values.isOneTimeCustomer ? 10 : 4
      },
      {
        title: intl.formatMessage({ defaultMessage: "Transportation Zone" }),
        name: "transportationZone",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Transportation Zone"
        }),
        type: "transportationZone",
        options: countryList,
        md: 6,
        hideField: formik.values.isOneTimeCustomer
      },
      {
        title: intl.formatMessage({ defaultMessage: "Street Address" }),
        name: "street1",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุที่อยู่"
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        md: 12,
        className: classes.street0,
        hint: (
          <div
            style={{ textAlign: "end", color: "#696969", marginRight: "-14px" }}
          >{`${formikOtcSoldTo?.values?.street1?.length || 0}/80`}</div>
        ),
        maxLength: 80
      },
      {
        title: intl.formatMessage({ defaultMessage: "  " }),
        name: "street2",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุที่อยู่"
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        md: 12,
        className: classes.street1,
        hint: (
          <div
            style={{
              textAlign: "end",
              color: "#696969",
              marginRight: "-14px",
              marginBottom: "-14px"
            }}
          >{`${formikOtcSoldTo?.values?.street2?.length || 0}/80`}</div>
        ),
        maxLength: 80
      },
      {
        title: isOtherCountry
          ? "City"
          : intl.formatMessage({ defaultMessage: "จังหวัด" }),
        name: "city",
        required: true,
        placeholder: isOtherCountry
          ? intl.formatMessage({ defaultMessage: "ระบุ City" })
          : intl.formatMessage({ defaultMessage: "เลือกจังหวัด" }),
        type: isOtherCountry
          ? FIELD_TYPE.SINGLE_INPUT
          : FIELD_TYPE.SINGLE_SELECT,
        options: city,
        md: 6,
        isDisabled: isDisableFiled(city),
        maxLength: 40
      },
      {
        title: intl.formatMessage({ defaultMessage: "เขต/อำเภอ" }),
        name: "district",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือกเขต/อำเภอ"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: district,
        md: 6,
        isDisabled: isDisableFiled(district),
        hideField: isOtherCountry
      },
      {
        title: intl.formatMessage({ defaultMessage: "แขวง/ตำบล" }),
        name: "subDistrict",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือกแขวง/ตำบล"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: subDistrict,
        md: 6,
        isDisabled: isDisableFiled(subDistrict),
        hideField: isOtherCountry
      },

      {
        title: isOtherCountry
          ? "Postal Code"
          : intl.formatMessage({ defaultMessage: "รหัสไปรษณีย์" }),
        name: "postalCode",
        placeholder: isOtherCountry
          ? intl.formatMessage({
              defaultMessage: "ระบุ Postal Code"
            })
          : intl.formatMessage({
              defaultMessage: "รหัสไปรษณีย์"
            }),
        type: isOtherCountry
          ? FIELD_TYPE.SINGLE_INPUT
          : FIELD_TYPE.SINGLE_SELECT,
        options: zipCode,
        md: 6,
        isDisabled: isOtherCountry
          ? false
          : isDisableFiled(formikOtcSoldTo.values.subDistrict),
        maxLength: 10
      },
      { type: "Headers", title: "Communication" },
      {
        title: intl.formatMessage({ defaultMessage: "Language" }),
        name: "language",
        placeholder: intl.formatMessage({
          defaultMessage: " "
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: languageList,
        md: 3
      },

      {
        title: intl.formatMessage({ defaultMessage: "Mobile Number" }),
        name: "mobileNo",
        required: false,
        placeholder: intl.formatMessage({
          defaultMessage: " "
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        md: 6.5,
        maxLength: 30
      },
      {
        title: intl.formatMessage({ defaultMessage: "Telephone No." }),
        name: "telephoneNo",
        placeholder: intl.formatMessage({
          defaultMessage: " "
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        md: 4,
        maxLength: 30
      },
      {
        title: intl.formatMessage({ defaultMessage: "Extension" }),
        name: "telephoneExtension",
        required: false,
        placeholder: intl.formatMessage({
          defaultMessage: " "
        }),
        type: FIELD_TYPE.NUMBER_INPUT,
        md: 2,
        nonBold: true,
        maxLength: 5
      },
      {
        title: intl.formatMessage({ defaultMessage: "Fax" }),
        name: "faxNo",
        required: false,
        placeholder: intl.formatMessage({
          defaultMessage: " "
        }),
        type: FIELD_TYPE.NUMBER_INPUT,
        md: 4,
        maxLength: 30
      },
      {
        title: intl.formatMessage({ defaultMessage: "Extension" }),
        name: "faxNoExt",
        required: false,
        placeholder: intl.formatMessage({
          defaultMessage: " "
        }),
        type: FIELD_TYPE.NUMBER_INPUT,
        md: 2,
        nonBold: true,
        maxLength: 5
      },
      {
        type: "Headers",
        title: "Tax Number",
        hideField: formik.values.isOneTimeCustomer
      },
      {
        title: isOtherCountry
          ? "Tax Number 1"
          : intl.formatMessage({
              defaultMessage: " เลขประจำตัวผู้เสียภาษี"
            }),
        titleInfo: !isOtherCountry
          ? intl.formatMessage({
              defaultMessage: "(13 หลัก)"
            })
          : null,
        name: isOtherCountry ? "taxNumber1" : "taxId",
        placeholder: isOtherCountry
          ? intl.formatMessage({ defaultMessage: "Tax Number 1" })
          : intl.formatMessage({ defaultMessage: "Tax Number 3" }),
        type: isOtherCountry
          ? FIELD_TYPE.SINGLE_INPUT
          : FIELD_TYPE.NUMBER_INPUT,
        md: 6,
        hideField: formik.values.isOneTimeCustomer,
        maxLength: isOtherCountry ? 16 : 13,
        className: isOtherCountry ? null : classes.taxNumberOtc
      },
      {
        title: isOtherCountry
          ? "Tax Number 2"
          : intl.formatMessage({ defaultMessage: " เลขที่สาขา" }),
        titleInfo: !isOtherCountry
          ? intl.formatMessage({ defaultMessage: "(5 หลัก)" })
          : null,
        name: isOtherCountry ? "taxNumber2" : "branchId",
        placeholder: isOtherCountry
          ? intl.formatMessage({ defaultMessage: "Tax Number 2" })
          : intl.formatMessage({ defaultMessage: "Tax Number 4" }),
        type: isOtherCountry
          ? FIELD_TYPE.SINGLE_INPUT
          : FIELD_TYPE.NUMBER_INPUT,
        md: 6,
        hideField: formik.values.isOneTimeCustomer,
        maxLength: isOtherCountry ? 11 : 5,
        className: isOtherCountry ? null : classes.taxNumberOtc
      },
      {
        type: "Headers",
        title: "Sales",
        hideField: formik.values.isOneTimeCustomer
      },
      {
        title: intl.formatMessage({ defaultMessage: "Sales Employee" }),
        name: "salesEmployee",
        required: true,
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ Sales Employee Code"
        }),
        type: FIELD_TYPE.NUMBER_INPUT,
        md: 6,
        maxLength: 10,
        hideField: formik.values.isOneTimeCustomer,
        className: classes.taxNumberOtc
      }
    ],
    [subDistrict, district, city, selectedZipCode]
  );

  return {
    formOneTimeData
  };
};

export default useOneTimeCustomer;
